import { Dispatch, FC, SetStateAction, useEffect, useRef } from 'react';
import { ColumnFilterApplyTemplateOptions } from 'primereact/column';
import { FilterError } from '../../../../../types/types';
import Button from '../../../../Button/Button';
import { checkFilterOnSubmit } from '../../../Filters/utils/checkFilterOnSubmit';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { isEmptyValue } from '../../../../../utils/managerUtils';
import { isValuelessMatchMode } from '../../../../../utils/filters/isValuelessMatchMode';
import { getIsBooleanColumnFilter } from './utils/getIsBooleanColumnFilter';
import { useColumnFilterRegistryContext } from '../../../../../hooks/context/useColumnFilterRegistryContext';
import { GridColumn } from '../../types';

export interface FilterApplyTemplateProps {
  options: ColumnFilterApplyTemplateOptions;
  column: GridColumn;
  filterErrors: Record<string, FilterError>;
  setFilterErrors: Dispatch<SetStateAction<Record<string, FilterError>>>;
}

const FilterApplyTemplate: FC<FilterApplyTemplateProps> = ({
  options,
  column,
  filterErrors,
  setFilterErrors,
}) => {
  // Holds the latest submit closure so registerApplyHandler can call current options/filterErrors without re-registering on every render.
  const submitRef = useRef<() => void>(() => undefined);

  const { t } = useTranslate();
  const { registerApplyHandler, markFilterSubmitted } =
    useColumnFilterRegistryContext();

  const onFilterSubmit = (
    options: ColumnFilterApplyTemplateOptions,
    column: GridColumn,
    filterErrors: Record<string, FilterError>
  ) => {
    const oldError = filterErrors[column.name];

    if (oldError !== null) {
      return;
    }

    const matchMode = options.filterModel.constraints[0].matchMode;

    // Unchecked boolean filter has value null; submit it as an explicit false instead of clearing.
    if (
      getIsBooleanColumnFilter(column) &&
      options.filterModel.constraints[0].value === null
    ) {
      options.filterModel.constraints[0].value = false;
    }

    // Submitting an empty value (for match modes that take a value) clears the filter — same outcome as the Clear button.
    if (
      !isValuelessMatchMode(matchMode) &&
      isEmptyValue(options.filterModel.constraints[0].value)
    ) {
      if (column.mandatoryFilter) {
        setFilterErrors((v) => ({
          ...v,
          [column.name]: [t('errors.emptyFilterError')],
        }));
        return;
      }
      options.filterModel.constraints[0].value = null;
      setFilterErrors((v) => ({ ...v, [column.name]: null }));
      options.filterApplyCallback();
      markFilterSubmitted(column.name);
      return;
    }

    const error = checkFilterOnSubmit(
      options.filterModel.constraints[0].value,
      matchMode,
      oldError,
      t
    );

    setFilterErrors((v) => ({ ...v, [column.name]: error }));

    if (error) {
      return;
    }

    if (isValuelessMatchMode(matchMode)) {
      options.filterModel.constraints[0].value = true;
    }

    options.filterApplyCallback();
    // Tell the registry this overlay closed via a real submit so FilterTemplate's unmount won't revert the just-applied draft.
    markFilterSubmitted(column.name);
  };

  submitRef.current = () => onFilterSubmit(options, column, filterErrors);

  // Registers a stable apply trigger that delegates to submitRef so Enter inside the overlay always runs the current submit logic.
  useEffect(() => {
    const unregister = registerApplyHandler(column.name, () =>
      submitRef.current()
    );
    return unregister;
  }, [column.name, registerApplyHandler]);

  return (
    <Button id="additional-buttons-trigger" onClick={() => submitRef.current()}>
      {t('filterApply')}
    </Button>
  );
};

export default FilterApplyTemplate;
